import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHero, Reveal, SectionEyebrow } from "@/components/Primitives";
import { fetchCourses } from "@/lib/api";
import { Award, BookOpen, Clock, Download, GraduationCap, PlayCircle, ArrowRight } from "lucide-react";
import SEO from "@/lib/seo";

const TESTIMONIALS = [
  { name: "Dr. Amara Okeke", role: "Director of Estates · Teaching Hospital, Lagos", quote: "The CCP cohort gave my team a vocabulary we should have had a decade ago." },
  { name: "Henrik Larsson", role: "Principal Architect · Studio Nord", quote: "The Cleanability for Architects programme rewired how our practice writes specifications." },
  { name: "Priya Iyer", role: "FM Innovation Lead · Bengaluru Tech Park", quote: "AI and Data in FM took our dashboards from decoration to decision-making." },
];

export default function Training() {
  const [courses, setCourses] = useState([]);
  useEffect(() => { fetchCourses().then(setCourses); }, []);

  return (
    <div data-testid="training-page">
      <SEO title="The Academy · The Cleanability Institute" description="Professional training programmes and certifications in cleanability." path="/training" />
      <PageHero
        testId="training-hero"
        eyebrow="The Academy"
        title="Training the first generation of cleanability professionals."
        lead="A professional academy offering credentials, specialist programmes and executive education across the discipline. Learners join cohorts from over thirty countries."
      />

      <section className="container-x border-y border-border py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {[["1,840", "Certified professionals"], ["8", "Active programmes"], ["32", "Countries enrolled"], ["97%", "Programme completion"]].map(([v, l], i) => (
            <div key={i} data-testid={`training-stat-${i}`}>
              <div className="font-display text-3xl md:text-4xl font-medium tracking-tight">{v}</div>
              <div className="text-xs text-muted-foreground mt-1">{l}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="container-x section-y">
        <SectionEyebrow>Programmes</SectionEyebrow>
        <h2 className="mt-6 font-display text-3xl md:text-5xl tracking-tight font-medium max-w-3xl text-balance">
          Cohorts running globally, year-round.
        </h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {courses.map((c, i) => (
            <Reveal key={c.id || c.code} delay={i * 0.05}>
              <Link to={`/training/${c.code}`} data-testid={`course-${c.code}`} className="rounded-2xl border border-border bg-card p-8 hover-lift h-full flex flex-col group">
                <div className="flex items-center justify-between">
                  <span className="font-mono-mini text-[hsl(var(--accent))]">{c.code}</span>
                  <span className="text-[10px] font-mono-mini px-2 py-1 rounded-md border border-border text-muted-foreground">{c.level}</span>
                </div>
                <h3 className="mt-4 font-display text-lg md:text-xl font-medium tracking-tight text-balance">{c.title}</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed flex-1">{c.description}</p>
                <div className="mt-6 flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {c.duration}</span>
                  <span className="inline-flex items-center gap-1.5"><BookOpen className="h-3.5 w-3.5" /> {c.modules} modules</span>
                </div>
                <div className="mt-6 pt-6 border-t border-border flex items-center justify-between">
                  <div className="font-display text-lg font-medium tracking-tight text-[hsl(var(--accent))]">£{Number(c.price || 0).toFixed(0)}</div>
                  <span className="text-xs font-medium underline-link inline-flex items-center gap-1">Details <ArrowRight className="h-3 w-3" /></span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-y border-border bg-secondary/30">
        <div className="container-x section-y">
          <SectionEyebrow>Credentials</SectionEyebrow>
          <h2 className="mt-6 font-display text-3xl md:text-5xl tracking-tight font-medium max-w-3xl text-balance">Recognised across the discipline.</h2>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            {["CCP", "CCA", "CDS", "HCS"].map((code) => (
              <div key={code} data-testid={`badge-${code}`} className="rounded-2xl border border-border bg-card p-8 text-center hover-lift">
                <div className="mx-auto h-16 w-16 rounded-full bg-[hsl(var(--accent))]/15 grid place-items-center">
                  <Award className="h-7 w-7 text-[hsl(var(--accent))]" />
                </div>
                <div className="mt-4 font-display text-xl font-medium">{code}</div>
                <div className="mt-1 text-xs text-muted-foreground">Cleanability Credential</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-x section-y border-t border-border">
        <SectionEyebrow>Voices from cohorts</SectionEyebrow>
        <h2 className="mt-6 font-display text-3xl md:text-5xl tracking-tight font-medium max-w-3xl text-balance">From practitioners, in their own words.</h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div data-testid={`testimonial-${i}`} className="rounded-2xl border border-border bg-card p-8 hover-lift h-full">
                <GraduationCap className="h-5 w-5 text-[hsl(var(--accent))]" />
                <p className="mt-6 font-serif-display text-lg leading-snug">"{t.quote}"</p>
                <div className="mt-6 pt-6 border-t border-border">
                  <div className="font-display font-medium">{t.name}</div>
                  <div className="text-xs text-muted-foreground">{t.role}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
